import React from 'react';
import { Callout, DocHeading, DocTable } from './components';

function PageFeatures({ navigate }) {
  // A csoportok = a bot felső fülei, a sorrend = az al-fülek sorrendje (manifest.js).
  const groups = [
    {
      tab: 'AUTOPILOT',
      items: [
        { id: 'autopilot',     title: 'Play',     desc: 'Load a saved route, check where you stand and press START. Loops, exits, +Kill marks, route folders, and a plain-English reason whenever a run stops.' },
        { id: 'route-builder', title: 'Editor',   desc: 'Build routes by walking them: record the path, set exits and difficulty levels, place stops on the path, mark monsters, fix bad stretches, or start from a ready-made map chain.' },
        { id: 'key-farm',      title: 'Key Farm', desc: 'Farm the Key of Prowess with 2 to 4 clients: one quest holder waits while the killers run a built-in path, switching between two difficulties every round.' },
        { id: 'auto-combat',   title: 'Combat',   desc: 'Build your skill rotation, set each skill’s pacing and aim, choose what to fight, and read the live reason when a skill is not firing.' },
        { id: 'party',         title: 'Party',    desc: 'Run 2 to 4 clients as a team that waits for each other at exits and bloodshed points, starts and stops together, and resurrects each other.' },
        { id: 'auto-revive',   title: 'Revive',   desc: 'Gets your character back up after a death using only the revive options you allow, and lets the route carry on afterwards.' },
      ],
    },
    {
      tab: 'FARM',
      items: [
        { id: 'farm-profiles', title: 'Profiles',   desc: 'Save your Loot, Gather, Sell and Melt setup — with item rules and bag locks — under a name, and switch farming styles with one click.' },
        { id: 'auto-loot',     title: 'Loot',       desc: 'Picks up what lies within reach, filtered by category, rarity and grade, with per-item TAKE / SKIP rules on top.' },
        { id: 'auto-gather',   title: 'Gather',     desc: 'Harvests plants and ores, opens the chest types you tick and uses free interactables as you pass, pausing a route just long enough to finish.' },
        { id: 'auto-sell',     title: 'Sell',       desc: 'Sells what you do not want without a merchant: category filter, per-item SELL / KEEP rules, a live list of what would go, and hard safety rules.' },
        { id: 'auto-melt',     title: 'Melt',       desc: 'Melts runes, gear and gems into dust and opens cubes — by hand or after every pickup — with Dry run and locked slots guarding against mistakes.' },
        { id: 'bloodshed',     title: 'Bloodshed',  desc: 'A live chest counter on screen during bloodshed runs, and collecting the chests one by one while the autopilot runs.' },
        { id: 'bag',           title: 'Bag',        desc: 'Lock bag slots so selling, melting and route teleport items never touch what is inside them.' },
        { id: 'item-lists',    title: 'Item Lists', desc: 'Every item in the game in one searchable list: mark single items to always or never pick up or sell, overruling the category filters.' },
        { id: 'farm-stats',    title: 'Stats',      desc: 'What your character gained this session in gold, andermant and farming currencies — the same numbers your account page shows.' },
      ],
    },
    {
      tab: 'QUESTS',
      items: [
        { id: 'quests', title: 'Daily Challenges & Fortune Hunters', desc: 'Rerolls a daily challenge until it offers the exact reward you want, and refreshes the Fortune Hunters until your chosen person sits in your chosen slot.' },
      ],
    },
    {
      tab: 'VISUALS',
      items: [
        { id: 'esp',    title: 'ESP',    desc: 'Monsters, mercenaries, gathering nodes, interactables, ground loot and incoming attack zones drawn straight into the world.' },
        { id: 'camera', title: 'Camera', desc: 'Zoom out past the game’s limit, change tilt, rotation and field of view, or detach the camera — kept across map changes.' },
        { id: 'names',  title: 'Names',  desc: 'Hide overhead player names, and show your own text instead of your character name in the inventory, character sheet and kill messages.' },
      ],
    },
    {
      tab: 'TOOLS · SETTINGS',
      items: [
        { id: 'fast-mount',   title: 'Fast Mount',   desc: 'Gets you on your mount without standing through the animation, optionally in a faster cloak, and keeps you mounted while a route runs.' },
        { id: 'auto-restart', title: 'Auto Restart', desc: 'Logs in with your chosen character and, together with the loader, restarts the game on a timer so long unattended sessions keep running.' },
        { id: 'settings',     title: 'Settings',     desc: 'Menu and unload keys, automatic reconnect status, several clients on one PC, data folder, crash reports, version and unload.' },
      ],
    },
  ];

  const link = (id, text) => (
    <button onClick={() => navigate(id)} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">{text}</button>
  );

  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Using the Bot</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">
        Features Overview
      </h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Everything MortisEngine does, grouped the way the menu groups it: one heading per main tab,
        one card per sub-tab. Click any card for the full guide to that feature.
      </p>

      <Callout type="info">
        <strong>Every feature has its own switch.</strong> Each one keeps its own settings, and they
        all keep working while a route runs. Nothing turns itself on &mdash; and a green dot after a
        tab name in the menu shows you what is switched on right now.
      </Callout>

      {groups.map(g => (
        <div key={g.tab}>
          <DocHeading>{g.tab}</DocHeading>
          <div className="grid sm:grid-cols-2 gap-4">
            {g.items.map(f => (
              <button
                key={f.id}
                onClick={() => navigate(f.id)}
                className="card card-interactive p-5 text-left group"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0" />
                    <p className="font-semibold text-white text-sm group-hover:text-brand-300 transition-colors">
                      {f.title}
                    </p>
                  </div>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                       className="w-3.5 h-3.5 text-gray-600 flex-shrink-0">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">{f.desc}</p>
              </button>
            ))}
          </div>
        </div>
      ))}

      <DocHeading>In What Order Should I Set Things Up?</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        If this is your first time, do it in this order. Each step is useful on its own, so you can
        stop anywhere and still have a working bot.
      </p>
      <DocTable
        headers={['#', 'Do this', 'Why here']}
        rows={[
          ['1', link('first-run', 'Open the menu and look around'), 'Get to know the tabs and the green dots before you switch anything on.'],
          ['2', link('auto-loot', 'Loot'), 'One switch, and you can see it working immediately.'],
          ['3', link('auto-combat', 'Combat'), 'Set your skills up while standing still, before anything moves on its own.'],
          ['4', link('route-builder', 'Build a route'), 'The biggest single step. Do one short map first.'],
          ['5', link('autopilot', 'Run it'), 'Watch one full lap before you leave it alone. Turn on Revive once it runs cleanly.'],
          ['6', link('bag', 'Lock your valuable bag slots'), 'Before anything is allowed to sell or melt.'],
          ['7', <>{link('auto-sell', 'Sell')} and {link('auto-melt', 'Melt')}</>, 'Only once the rest works: these are the ones that cannot be undone. Start with Dry run on.'],
          ['8', link('farm-profiles', 'Save a profile'), 'When a setup works, store it, so you can switch between farming styles later.'],
        ]}
      />
    </div>
  );
}

export default PageFeatures;
